import React, { useState } from 'react';
import { 
  Activity, Heart, Video, Calendar, MessageCircle, TrendingUp, 
  AlertTriangle, FileText, Upload, Wind, HelpCircle, Pill,
  Target, ChevronLeft, ChevronRight, CheckCircle, Clock, Home, FlaskConical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { PatientLayout } from './PatientLayout';
import { useMyDashboard } from '../../lib/hooks/useDashboards';
import { useMyCombinedTrends } from '../../lib/hooks/useTrends';
import { useAuth } from '../../lib/hooks/useAuth';
import { useNotifications } from '../../lib/hooks/useNotifications';
import { useAppointments } from '../../lib/hooks/useAppointments';
import { LoadingState } from '../common/LoadingState';

interface PatientDashboardProps {
  onNavigate?: (view: 'dashboard' | 'exercise' | 'vitals' | 'reports' | 'messaging' | 'appointments' | 'library' | 'settings' | 'notifications' | 'symptoms' | 'symptoms-manage' | 'hometest' | 'spirometry' | 'sixmwt' | 'labreport' | 'discharge-summary' | 'medication-log' | 'exercise-log' | 'hometest-log' | 'hometest-manage') => void;
  onLogout?: () => void;
}

export function PatientDashboard({ onNavigate, onLogout }: PatientDashboardProps) {
  // Get authenticated user data
  const { user, loading: authLoading } = useAuth();
  
  // ✅ FIX: Use /me/dashboard endpoint instead of /patients/{id}/dashboard-summary
  // This ensures patient gets their own data without needing patient_id
  const { data: dashboardData, isLoading, error, refetch } = useMyDashboard(!authLoading);
  
  // ✅ FIX: Fetch combined trends data (FEV1, 6MWT, SpO2) using /me/trends
  const { data: trendsData, isLoading: trendsLoading } = useMyCombinedTrends(
    6, // 6 weeks
    !authLoading
  );
  
  // Fetch notifications/alerts from API
  const { data: notificationsData } = useNotifications();
  
  // ✅ FIX: Fetch all appointments for the patient using /me/appointments
  const { data: appointmentsData } = useAppointments(
    undefined, // no filters - get all appointments
    { enabled: !authLoading }
  );
  
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [currentView, setCurrentView] = useState<string>('dashboard');
  
  // Show loading while auth is loading
  if (authLoading) {
    return (
      <PatientLayout
        patientName={'Patient'}
        adherenceScore={0}
        currentView={'dashboard'}
        onNavigate={onNavigate}
        onLogout={onLogout}
        notificationCount={0}
      >
        <LoadingState message="Loading..." />
      </PatientLayout>
    );
  }

  // Handle navigation
  const handleNavigation = (view: string) => {
    setCurrentView(view);
    if (onNavigate) {
      onNavigate(view as any);
    }
  };

  // Show loading state
  if (isLoading || trendsLoading) {
    return (
      <PatientLayout
        patientName={user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
        currentView={currentView}
        onNavigate={handleNavigation}
        onLogout={onLogout}
        notificationCount={0}
      >
        <LoadingState message="Loading your dashboard..." />
      </PatientLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <PatientLayout
        patientName={user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
        currentView={currentView}
        onNavigate={handleNavigation}
        onLogout={onLogout}
        notificationCount={0}
      >
        <div className="p-4">
          <Alert variant="destructive" className="border-l-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Failed to load dashboard</AlertTitle>
            <AlertDescription>
              {error?.message || "An error occurred while loading your dashboard"}
              <div className="mt-3">
                <Button onClick={() => refetch()} variant="outline" size="sm">
                  Try Again
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </PatientLayout>
    );
  }

  
  // Extract data from API response with fallbacks
  const clinicalScores = {
    // ✅ FIX: Extract real FEV1 data from backend
    fev1: dashboardData?.clinical_scores?.fev1 || dashboardData?.fev1_latest || { 
      value: dashboardData?.last_fev1 || 0, 
      unit: '%', 
      last_test: dashboardData?.last_fev1_date || 'N/A', 
      change: dashboardData?.fev1_change || 0, 
      normal: '80-120%' 
    },
    // ✅ FIX: Extract real 6MWT data from backend
    sixMWT: dashboardData?.clinical_scores?.sixMWT || dashboardData?.sixmwt_latest || { 
      value: dashboardData?.last_6mwd || 0, 
      unit: 'm', 
      last_test: dashboardData?.last_6mwd_date || 'N/A', 
      change: dashboardData?.six_mwd_change || 0, 
      normal: '400-700m' 
    },
    // ✅ FIX: Extract real SpO2 data from backend
    spo2: dashboardData?.clinical_scores?.spo2 || dashboardData?.spo2_latest || { 
      value: dashboardData?.last_spo2 || 0, 
      unit: '%', 
      last_test: dashboardData?.last_spo2_date || 'N/A', 
      change: 0, 
      normal: '95-100%' 
    },
  };

  const recentSymptoms = dashboardData?.recent_symptoms || [];

  // ✅ FIX: Adherence Data from API - map from actual backend response
  const medicationAdherence = {
    percentage: dashboardData?.adherence?.medication?.percentage || 0,
    takenToday: dashboardData?.adherence?.medication?.takenToday || 0,
    totalToday: dashboardData?.adherence?.medication?.totalToday || 0,
    status: dashboardData?.adherence?.medication?.status || 
            ((dashboardData?.adherence?.medication?.percentage || 0) >= 90 ? 'excellent' as const :
             (dashboardData?.adherence?.medication?.percentage || 0) >= 75 ? 'good' as const : 
             'poor' as const),
    tracking: '7-day tracking' // From design
  };

  const exerciseAdherence = {
    percentage: dashboardData?.adherence?.exercise?.percentage || 0,
    completedToday: dashboardData?.adherence?.exercise?.completedToday || 0,
    totalToday: dashboardData?.adherence?.exercise?.totalToday || 0,
    status: dashboardData?.adherence?.exercise?.status || 
            ((dashboardData?.adherence?.exercise?.percentage || 0) >= 90 ? 'excellent' as const :
             (dashboardData?.adherence?.exercise?.percentage || 0) >= 75 ? 'good' as const : 
             'poor' as const),
    tracking: '7-day tracking' // From design
  };

  const homeTestAdherence = {
    percentage: dashboardData?.adherence?.hometest?.percentage || 0,
    completedThisWeek: dashboardData?.adherence?.hometest?.completedThisWeek || 0,
    totalThisWeek: dashboardData?.adherence?.hometest?.totalThisWeek || 0,
    status: dashboardData?.adherence?.hometest?.status || 
            ((dashboardData?.adherence?.hometest?.percentage || 0) >= 90 ? 'excellent' as const :
             (dashboardData?.adherence?.hometest?.percentage || 0) >= 75 ? 'good' as const : 
             'poor' as const),
    tracking: '30-day tracking' // From design
  };

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return '#14CAF4';
    if (adherence >= 75) return '#F07D13';
    return '#EF4444';
  };

  const getAdherenceStatus = (adherence: number) => {
    if (adherence >= 90) return { label: 'Excellent', risk: 'Low Risk', icon: CheckCircle };
    if (adherence >= 75) return { label: 'Good', risk: 'Moderate Risk', icon: AlertTriangle };
    return { label: 'Poor', risk: 'High Risk', icon: AlertTriangle };
  };

  // Upcoming Tasks from API
  const upcomingTasks = dashboardData?.upcoming_tasks || [];

  // ✅ NEW: Extract missed/incomplete tasks - with null safety
  const incompleteTasks = upcomingTasks.filter(task => !task?.completed);
  const hasMissedTasks = incompleteTasks.length > 0;

  // ✅ NEW: Check for concerning symptoms - with null safety
  const hasConcerningSymptoms = recentSymptoms.length > 0 && 
    recentSymptoms.some(s => (s?.breathlessness || 0) >= 7);

  // Trend Data (6 weeks) - Use API data or fallback
  const hasTrendData = trendsData?.data && trendsData.data.length > 0 && 
    trendsData.data.some(item => (item?.fev1 || 0) > 0 || (item?.sixMWD || 0) > 0 || (item?.spo2 || 0) > 0);
    
  const trendData = trendsData?.data && trendsData.data.length > 0 
    ? trendsData.data.map((item, idx) => ({
        week: `W${idx + 1}`,
        fev1: item?.fev1 || 0,
        sixMWD: item?.sixMWD || 0,
        spo2: item?.spo2 || 0,
      })) 
    : [
        { week: 'W1', fev1: 0, sixMWD: 0, spo2: 0 },
        { week: 'W2', fev1: 0, sixMWD: 0, spo2: 0 },
        { week: 'W3', fev1: 0, sixMWD: 0, spo2: 0 },
        { week: 'W4', fev1: 0, sixMWD: 0, spo2: 0 },
        { week: 'W5', fev1: 0, sixMWD: 0, spo2: 0 },
        { week: 'W6', fev1: 0, sixMWD: 0, spo2: 0 },
      ];

  // Education Tips (static educational content - acceptable to keep hardcoded)
  const educationTips = [
    {
      title: 'Nutrition for Lung Health',
      content: 'Eat antioxidant-rich foods like berries, leafy greens, and fatty fish to reduce inflammation and support lung function.',
      icon: '🥗',
      color: 'bg-[#14CAF4]/10',
    },
    {
      title: 'Breathing Techniques',
      content: 'Practice pursed-lip breathing: Inhale through nose for 2 counts, exhale through pursed lips for 4 counts. Helps control breathlessness.',
      icon: '🫁',
      color: 'bg-accent/10',
    },
    {
      title: 'Energy Conservation',
      content: 'Plan activities during your peak energy times. Take frequent breaks, and use assistive devices to reduce effort.',
      icon: '⚡',
      color: 'bg-primary/10',
    },
    {
      title: 'Support Groups',
      content: 'Join our monthly virtual support group every first Saturday. Connect with others on similar journeys and share experiences.',
      icon: '👥',
      color: 'bg-[#FEEBDA]',
    },
  ];

  // Alerts from Notifications API - with proper null checks
  const alerts = (notificationsData?.notifications || [])
    .filter(n => !n?.is_read && (n?.priority === 'HIGH' || n?.notification_type === 'ALERT'))
    .slice(0, 3) // Show top 3 alerts
    .map(n => ({
      id: n?.id || 0,
      type: n?.priority === 'HIGH' ? 'warning' as const : 'info' as const,
      title: n?.title || 'Alert',
      message: n?.message || '',
      timestamp: formatRelativeTime(n?.created_at || new Date().toISOString()),
    }));

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const handleLogVitals = () => {
    toast.info('Opening vitals logging...');
    onNavigate?.('vitals');
  };

  const handleViewProgress = () => {
    toast.info('Loading progress reports...');
    onNavigate?.('reports');
  };

  const handleOpenChat = () => {
    toast.info('Opening chat with your healthcare team...');
    onNavigate?.('messaging');
  };

  const handleStartHomeTest = () => {
    toast.info('Opening home test options...');
    onNavigate?.('hometest');
  };

  const handleUploadLabReport = () => {
    toast.info('Opening lab report upload...');
    onNavigate?.('labreport');
  };

  const handleViewExercisePlan = () => {
    toast.info('Loading your personalized exercise plan...');
    onNavigate?.('library');
  };

  const handleBreathingTechniques = () => {
    toast.info('Opening breathing techniques guide...');
    onNavigate?.('exercise');
  };

  const handleAskCoordinator = () => {
    toast.info('Connecting you with your care coordinator...');
    onNavigate?.('messaging');
  };

  const handleLogSymptoms = () => {
    toast.info('Opening symptom logging...');
    onNavigate?.('symptoms');
  };

  const handleLogMedication = () => {
    toast.info('Opening medication tracker...');
    onNavigate?.('medication-log');
  };

  const handleLogExercise = () => {
    // Navigate to exercise log management page
    onNavigate?.('exercise-log');
  };

  const handleLogHomeTest = () => {
    toast.info('Opening home test tracker...');
    onNavigate?.('hometest-log');
  };

  const handleCompleteTask = (taskId: number) => {
    toast.success('Task marked as complete!');
  };

  const nextTip = () => {
    setCurrentTipIndex((prev) => (prev + 1) % educationTips.length);
  };

  const prevTip = () => {
    setCurrentTipIndex((prev) => (prev - 1 + educationTips.length) % educationTips.length);
  };

  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-[#14CAF4]';
    if (change < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return '↑';
    if (change < 0) return '↓';
    return '−';
  };

  const getInitials = (name: string) => {
    if(!name)
      return;
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }


  return (
    <PatientLayout
      patientName={user?.full_name || 'Patient'}
      adherenceScore={user?.adherence || 0}
      currentView={currentView}
      onNavigate={handleNavigation}
      onLogout={onLogout}
      notificationCount={alerts.length}
    >
      <div className="pb-20">
        {/* Header */}
        <div className="bg-primary p-6 rounded-b-3xl shadow-md mt-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-primary-foreground/80">Good Morning,</p>
              <h1 className="text-primary-foreground" style={{fontSize:"18px", fontWeight:"bold"}}>{user?.full_name || 'N/A'} 👋</h1>
            </div>
            <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center">
              <span className="text-accent-foreground">{getInitials(user?.full_name) || "PT"}</span>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4 ">
          {/* Alerts Section */}
          {alerts.length > 0 && (
            <div className="space-y-2">
              {alerts.map((alert, idx) => (
                <Alert 
                  key={alert.id || idx} 
                  variant={alert.type === 'warning' ? 'destructive' : 'default'}
                  className="border-l-4"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>{alert.title}</AlertTitle>
                  <AlertDescription>
                    {alert.message}
                    <p className="text-muted-foreground mt-1">{alert.timestamp}</p>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 " >
            {/* Appointments Section */}
            <div >
              <Card className="shadow-lg  h-full flex flex-col">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Upcoming Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Filter upcoming appointments (not cancelled, scheduled date >= today)
                    const today = new Date();
                    
                    const upcomingAppointments = (appointmentsData || [])
                      .filter(apt => {
                        if (!apt?.scheduled_date) return false;
                        const aptDate = new Date(apt.scheduled_date);
                        return aptDate >= today && apt?.status !== 'Cancelled';
                      })
                      .sort((a, b) => new Date(a?.scheduled_date || 0).getTime() - new Date(b?.scheduled_date || 0).getTime())
                      .slice(0, 5); // Show up to 5 upcoming appointments
                    
                    if (upcomingAppointments.length === 0) {
                      return (
                        <div className="text-center py-6">
                          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">No upcoming appointments</p>
                          <Button 
                            variant="link" 
                            className="text-primary mt-2"
                            onClick={() => onNavigate?.('appointments')}
                          >
                            View All Appointments
                          </Button>
                        </div>
                      );
                    }

                    return (
                      <div className="space-y-3">
                        {upcomingAppointments.map((appointment, idx) => {
                          const aptDate = new Date(appointment.scheduled_date);
                          const formattedDate = aptDate.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          });
                          const formattedTime = aptDate.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          });
                          
                          return (
                            <div 
                              key={appointment?.id || idx}
                              className="p-3 bg-secondary rounded-lg border border-border hover:border-primary transition-colors"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <p className="font-medium text-foreground">{appointment?.doctor_name || 'Doctor'}</p>
                                  <p className="text-sm text-muted-foreground">{appointment?.appointment_type || 'Appointment'}</p>
                                </div>
                                <Badge 
                                  variant={appointment?.status === 'Scheduled' ? 'default' : 'secondary'}
                                  className="ml-2"
                                >
                                  {appointment?.status || 'Scheduled'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-primary">
                                <Clock className="w-4 h-4" />
                                <span>{formattedDate} at {formattedTime}</span>
                              </div>
                              {appointment?.reason && (
                                <p className="text-sm text-muted-foreground mt-1">{appointment.reason}</p>
                              )}
                            </div>
                          );
                        })}
                        <Button 
                          variant="link" 
                          className="w-full mt-2"
                          onClick={() => onNavigate?.('appointments')}
                        >
                          View All Appointments
                        </Button>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Overview Cards Grid */}
            <div >
              {/* Upcoming Tasks */}
              <Card className="shadow-lg  h-full flex flex-col">
                <CardHeader>
                  <CardTitle className="text-primary flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Upcoming Tasks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {upcomingTasks.length === 0 ? (
                    <div className="text-center py-6">
                      <Target className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                      <p className="text-muted-foreground">No upcoming tasks</p>
                      <p className="text-muted-foreground">You're all caught up!</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {upcomingTasks.slice(0, 3).map((task, idx) => (
                          <div 
                            key={task?.id || idx} 
                            className="flex items-center gap-2 p-2 bg-secondary rounded-lg"
                          >
                            <button
                              onClick={() => handleCompleteTask(task?.id || 0)}
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                task?.completed 
                                  ? 'bg-[#14CAF4] border-[#14CAF4]' 
                                  : 'border-muted-foreground'
                              }`}
                            >
                              {task?.completed && <CheckCircle className="w-4 h-4 text-white" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <p className="text-foreground truncate">{task?.task || 'No description'}</p>
                              <p className="text-muted-foreground">{task?.due || 'No due date'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button 
                        variant="link" 
                        className="w-full mt-2 text-primary"
                        onClick={() => toast.info('Viewing all tasks...')}
                      >
                        View All Tasks
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Adherence Tracking Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-primary font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Adherence Tracking
              </h2>
            </div>
            {/* Medication Adherence Strip */}
            <Card 
              className="shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow" 
              style={{ borderLeftColor: getAdherenceColor(medicationAdherence.percentage) }}
              onClick={handleLogMedication}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Pill className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">Medication Adherence</h4>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-lg font-semibold"
                            style={{ color: getAdherenceColor(medicationAdherence.percentage) }}
                          >
                            {medicationAdherence.percentage}%
                          </span>
                          <Badge 
                            style={{ 
                              backgroundColor: getAdherenceColor(medicationAdherence.percentage),
                              color: '#fff'
                            }}
                            className="text-xs"
                          >
                            {getAdherenceStatus(medicationAdherence.percentage).label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Today: {medicationAdherence.takenToday}/{medicationAdherence.totalToday} doses</span>
                        <span>7-day tracking</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="h-1.5 rounded-full transition-all"
                          style={{ 
                            width: `${medicationAdherence.percentage}%`,
                            backgroundColor: getAdherenceColor(medicationAdherence.percentage)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                </div>
              </CardContent>
            </Card>

            {/* Exercise Adherence Strip */}
            <Card 
              className="shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow" 
              style={{ borderLeftColor: getAdherenceColor(exerciseAdherence.percentage) }}
              onClick={handleLogExercise}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Activity className="w-5 h-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">Exercise Adherence</h4>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-lg font-semibold"
                            style={{ color: getAdherenceColor(exerciseAdherence.percentage) }}
                          >
                            {exerciseAdherence.percentage}%
                          </span>
                          <Badge 
                            style={{ 
                              backgroundColor: getAdherenceColor(exerciseAdherence.percentage),
                              color: '#fff'
                            }}
                            className="text-xs"
                          >
                            {getAdherenceStatus(exerciseAdherence.percentage).label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>Today: {exerciseAdherence.completedToday}/{exerciseAdherence.totalToday} exercises</span>
                        <span>7-day tracking</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="h-1.5 rounded-full transition-all"
                          style={{ 
                            width: `${exerciseAdherence.percentage}%`,
                            backgroundColor: getAdherenceColor(exerciseAdherence.percentage)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                </div>
              </CardContent>
            </Card>

            {/* Home Test Adherence Strip */}
            <Card 
              className="shadow-sm border-l-4 cursor-pointer hover:shadow-md transition-shadow" 
              style={{ borderLeftColor: getAdherenceColor(homeTestAdherence.percentage) }}
              onClick={handleLogHomeTest}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-full bg-[#14CAF4]/10 flex items-center justify-center">
                      <Wind className="w-5 h-5 text-[#14CAF4]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium">Home Test Adherence</h4>
                        <div className="flex items-center gap-2">
                          <span 
                            className="text-lg font-semibold"
                            style={{ color: getAdherenceColor(homeTestAdherence.percentage) }}
                          >
                            {homeTestAdherence.percentage}%
                          </span>
                          <Badge 
                            style={{ 
                              backgroundColor: getAdherenceColor(homeTestAdherence.percentage),
                              color: '#fff'
                            }}
                            className="text-xs"
                          >
                            {getAdherenceStatus(homeTestAdherence.percentage).label}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                        <span>This week: {homeTestAdherence.completedThisWeek}/{homeTestAdherence.totalThisWeek} tests</span>
                        <span>30-day tracking</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="h-1.5 rounded-full transition-all"
                          style={{ 
                            width: `${homeTestAdherence.percentage}%`,
                            backgroundColor: getAdherenceColor(homeTestAdherence.percentage)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Clinical Scores Summary Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Clinical Score Summaries
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clinicalScores.fev1.last_test === 'N/A' && clinicalScores.sixMWT.last_test === 'N/A' && clinicalScores.spo2.last_test === 'N/A' ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">No clinical test results yet</p>
                  <p className="text-muted-foreground">Complete your first home test to see your scores</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={handleStartHomeTest}
                  >
                    Start Home Test
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* FEV1 */}
                  <div className="p-4 bg-primary/10 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-muted-foreground">FEV1</p>
                      <Badge variant="outline" className="bg-primary/20 text-primary border-primary">
                        Last: {clinicalScores.fev1.last_test}
                      </Badge>
                    </div>
                    {clinicalScores.fev1.last_test === 'N/A' ? (
                      <div className="text-center py-2">
                        <p className="text-muted-foreground">No data</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-end gap-2">
                          <h2 className="text-primary">
                            {clinicalScores.fev1.value}{clinicalScores.fev1.unit}
                          </h2>
                          <span className={`${getChangeColor(clinicalScores.fev1.change || 0)} mb-1`}>
                            {getChangeIcon(clinicalScores.fev1.change || 0)} {Math.abs(clinicalScores.fev1.change || 0)}%
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-2">
                          Normal: {clinicalScores.fev1.normal}
                        </p>
                      </>
                    )}
                  </div>

                  {/* 6MWT */}
                  <div className="p-4 bg-accent/10 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-muted-foreground">6-Min Walk Test</p>
                      <Badge variant="outline" className="bg-accent/20 text-accent border-accent">
                        Last: {clinicalScores.sixMWT.last_test}
                      </Badge>
                    </div>
                    {clinicalScores.sixMWT.last_test === 'N/A' ? (
                      <div className="text-center py-2">
                        <p className="text-muted-foreground">No data</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-end gap-2">
                          <h2 className="text-accent">
                            {clinicalScores.sixMWT.value}{clinicalScores.sixMWT.unit}
                          </h2>
                          <span className={`${getChangeColor(clinicalScores.sixMWT.change || 0)} mb-1`}>
                            {getChangeIcon(clinicalScores.sixMWT.change || 0)} {Math.abs(clinicalScores.sixMWT.change || 0)}m
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-2">
                          Normal: {clinicalScores.sixMWT.normal}
                        </p>
                      </>
                    )}
                  </div>

                  {/* SpO2 */}
                  <div className="p-4 bg-[#14CAF4]/10 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-muted-foreground">SpO₂</p>
                      <Badge variant="outline" className="bg-[#14CAF4]/20 text-[#14CAF4] border-[#14CAF4]">
                        {clinicalScores.spo2.last_test}
                      </Badge>
                    </div>
                    {clinicalScores.spo2.last_test === 'N/A' ? (
                      <div className="text-center py-2">
                        <p className="text-muted-foreground">No data</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-end gap-2">
                          <h2 className="text-[#14CAF4]">
                            {clinicalScores.spo2.value}{clinicalScores.spo2.unit}
                          </h2>
                          <span className={`${getChangeColor(clinicalScores.spo2.change || 0)} mb-1`}>
                            {getChangeIcon(clinicalScores.spo2.change || 0)}
                          </span>
                        </div>
                        <p className="text-muted-foreground mt-2">
                          Normal: {clinicalScores.spo2.normal}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Symptoms */}
              <div className="mt-4 p-4 bg-[#FEEBDA] rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-primary flex items-center gap-2">
                    <Heart className="w-4 h-4" />
                    Recent Symptoms (Last 7 Days)
                  </h3>
                  <Button 
                    variant="link" 
                    className="text-primary text-sm p-0 h-auto"
                    onClick={() => onNavigate?.('symptoms-manage')}
                  >
                    View All
                  </Button>
                </div>
                {recentSymptoms.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground">No recent symptoms logged</p>
                    <Button 
                      variant="link" 
                      className="text-primary mt-2"
                      onClick={handleLogSymptoms}
                    >
                      Log Symptoms
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentSymptoms.slice(0, 3).map((symptom, idx) => {
                      // ✅ FIX: Extract the highest symptom severity using correct field names from backend with null checks
                      const symptoms = [];
                      if ((symptom?.breathlessness || 0) > 0) symptoms.push({ name: 'Breathlessness', value: symptom.breathlessness });
                      if ((symptom?.cough || 0) > 0) symptoms.push({ name: 'Cough', value: symptom.cough });
                      if ((symptom?.fatigue || 0) > 0) symptoms.push({ name: 'Fatigue', value: symptom.fatigue });
                      if ((symptom?.chest_pain || 0) > 0) symptoms.push({ name: 'Chest Pain', value: symptom.chest_pain });
                      if ((symptom?.energy_level || 0) > 0) symptoms.push({ name: 'Low Energy', value: 10 - symptom.energy_level }); // Invert: higher is worse
                      if ((symptom?.sleep_quality || 0) > 0) symptoms.push({ name: 'Poor Sleep', value: 10 - symptom.sleep_quality }); // Invert: higher is worse
                      
                      const topSymptom = symptoms.length > 0 
                        ? symptoms.reduce((max, s) => s.value > max.value ? s : max, symptoms[0])
                        : null;

                      const getSeverityLabel = (val: number) => {
                        if (val <= 3) return 'Mild';
                        if (val <= 6) return 'Moderate';
                        return 'Severe';
                      };

                      const getSeverityColor = (val: number) => {
                        if (val <= 3) return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
                        if (val <= 6) return 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]';
                        return 'bg-destructive/10 text-destructive border-destructive';
                      };

                      return (
                        <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div>
                            <p className="text-foreground text-sm">
                              {topSymptom ? `${topSymptom.name} (${topSymptom.value}/10)` : 'No symptoms'}
                              {symptoms.length > 1 && ` +${symptoms.length - 1} more`}
                            </p>
                            <p className="text-muted-foreground text-xs">{symptom?.date || 'N/A'}</p>
                          </div>
                          {topSymptom && (
                            <Badge 
                              variant="outline" 
                              className={getSeverityColor(topSymptom.value)}
                            >
                              {getSeverityLabel(topSymptom.value)}
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                    {recentSymptoms.length > 3 && (
                      <Button 
                        variant="link" 
                        className="w-full text-primary text-sm"
                        onClick={() => onNavigate?.('symptoms-manage')}
                      >
                        View {recentSymptoms.length - 3} more
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Trends Section */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                6-Week Progress Trends
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {!hasTrendData ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground mb-2">No trend data available yet</p>
                  <p className="text-muted-foreground">Complete tests regularly to track your progress over time</p>
                  <div className="flex gap-3 justify-center mt-4">
                    <Button 
                      variant="outline"
                      onClick={handleStartHomeTest}
                    >
                      Start Home Test
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleLogVitals}
                    >
                      Log Vitals
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* FEV1 Trend */}
                  <div>
                    <h3 className="text-primary mb-3 font-medium">FEV1 Trend (%)</h3>
                    <div className="h-56 bg-white p-4 rounded-lg border border-border">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <YAxis 
                            domain={[50, 75]} 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #E5E5E3',
                              borderRadius: '8px',
                              padding: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="fev1" 
                            stroke="#2762AC" 
                            strokeWidth={2} 
                            dot={{ fill: '#2762AC', r: 4, strokeWidth: 2, stroke: '#fff' }} 
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 6MWD Trend */}
                  <div>
                    <h3 className="text-[#F07D13] mb-3 font-medium">6-Minute Walk Distance (m)</h3>
                    <div className="h-56 bg-white p-4 rounded-lg border border-border">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <YAxis 
                            domain={[300, 450]} 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #E5E5E3',
                              borderRadius: '8px',
                              padding: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="sixMWD" 
                            stroke="#F07D13" 
                            strokeWidth={2} 
                            dot={{ fill: '#F07D13', r: 4, strokeWidth: 2, stroke: '#fff' }} 
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* SpO2 Trend */}
                  <div>
                    <h3 className="text-[#14CAF4] mb-3 font-medium">SpO₂ Trend (%)</h3>
                    <div className="h-56 bg-white p-4 rounded-lg border border-border">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E3" />
                          <XAxis 
                            dataKey="week" 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <YAxis 
                            domain={[92, 100]} 
                            tick={{ fontSize: 12, fill: '#494949' }} 
                            stroke="#494949" 
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#fff', 
                              border: '1px solid #E5E5E3',
                              borderRadius: '8px',
                              padding: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="spo2" 
                            stroke="#14CAF4" 
                            strokeWidth={2} 
                            dot={{ fill: '#14CAF4', r: 4, strokeWidth: 2, stroke: '#fff' }} 
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleLogSymptoms}
                >
                  <Heart className="w-5 h-5" />
                  <span className="text-xs">Log Symptoms</span>
                </Button>

                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleLogVitals}
                >
                  <Activity className="w-5 h-5" />
                  <span className="text-xs">Daily Vitals</span>
                </Button>

                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleStartHomeTest}
                >
                  <Wind className="w-5 h-5" />
                  <span className="text-xs">Home Test</span>
                </Button>

                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleUploadLabReport}
                >
                  <Upload className="w-5 h-5" />
                  <span className="text-xs">Lab Report</span>
                </Button>

                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleViewExercisePlan}
                >
                  <FileText className="w-5 h-5" />
                  <span className="text-xs">Exercise Plan</span>
                </Button>

                <Button 
                  className="bg-primary hover:bg-primary/90 h-12 rounded-xl flex-col gap-1 px-3"
                  onClick={handleAskCoordinator}
                >
                  <HelpCircle className="w-5 h-5" />
                  <span className="text-xs">Ask Help</span>
                </Button>

                <Button 
                  className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 h-12 rounded-xl flex-col gap-1 px-3 text-white"
                  onClick={handleLogMedication}
                >
                  <Pill className="w-5 h-5" />
                  <span className="text-xs">Log Medicine</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Education Carousel */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary">Health Education</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <div className={`p-6 ${educationTips[currentTipIndex].color} rounded-xl`}>
                  <div className="text-center mb-4">
                    <span className="text-6xl">{educationTips[currentTipIndex].icon}</span>
                  </div>
                  <h3 className="text-primary text-center mb-3">
                    {educationTips[currentTipIndex].title}
                  </h3>
                  <p className="text-foreground text-center">
                    {educationTips[currentTipIndex].content}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={prevTip}
                    className="text-primary"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Previous
                  </Button>

                  <div className="flex gap-2">
                    {educationTips.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-2 h-2 rounded-full ${
                          idx === currentTipIndex ? 'bg-primary' : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={nextTip}
                    className="text-primary"
                  >
                    Next
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Streak */}
          <Card className="shadow-lg bg-gradient-to-r from-[#2762AC] to-[#14CAF4]">
            <CardContent className="p-6 text-center">
              <p className="text-primary-foreground/80">Current Streak</p>
              <h2 className="text-primary-foreground">{user?.streak || 0} Days</h2>
              <p className="text-primary-foreground/80">Keep it up! 🔥</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </PatientLayout>
  );
}